// src/domains/prices.ts —— 06 A-10「我的售价」原生域（0045 代理加价定价，切片 s.pr）：
// GET /api/agent/prices 读取；每个上架产品一行开卡售价 + 一行充值费率；留空 = 沿用上级价；前端预检（格式 / 小数位 / ≥ 上级价）；
// PUT /api/agent/prices 只提交改动项；1310 按 data.items 逐行标红；4220 product_id（产品刚下架）与 1012（上级并发调价）→ 刷新视图保留草稿。
// 跨域契约：readonlyPriceRows / uplineText 供 subs 域「下级售价」只读弹层复用（同一行口径，DRY）。
import { ApiError } from '../../../shared/src/api/client';
import type { AgentPricesView } from '../../../shared/src/api/types';
import type { Dict, Lang } from '../../../shared/src/dc';
import { interpolate } from '../../../shared/src/errors';
import { belowMarks, checkDrafts, draftsOf, fmtDepositRate, fmtOpenPrice, priceBelowItems, pricesPatch, trimDecimal, type BelowMarks, type PriceDraftError, type PriceDrafts } from '../../../shared/src/prices';
import { I18N_PRICES } from '../i18n/prices';
import type { AgentState } from '../state';
import { KEYS, MODAL_KEYS } from './prices.keys';
import { str, type AgentCtx, type AgentDomain, type InputEvent } from './types';

export interface PricesSlice {
  view: AgentPricesView | null;
  drafts: PriceDrafts;
  marks: BelowMarks; // 服务端 1310 标红（值 = 上级当前生效价）
  error: string; // 底部红条
}

declare module '../state' {
  interface AgentSlices {
    pr?: PricesSlice;
  }
}

const EMPTY_MARKS: BelowMarks = { open: {}, deposit: null };
const PR0: PricesSlice = { view: null, drafts: { open: {}, deposit: '' }, marks: EMPTY_MARKS, error: '' };
const DRAFT_MAX = 24; // 输入长度上限（防粘贴超长串；合法值远短于此）

export function pricesOf(s: AgentState): PricesSlice {
  return s.pr ?? PR0;
}

function setPr(ctx: AgentCtx, patch: Partial<PricesSlice>): void {
  ctx.host.setState({ pr: { ...pricesOf(ctx.host.state), ...patch } });
}

// ---- 展示口径（我的售价页与下级只读弹层共用） ----

const RED = '#d1434b';
const ROW = 'border-top:1px solid #f0f3f1';
const ROW_BELOW = 'border-top:1px solid #f0f3f1;background:#fdf3f3';
const INPUT = 'width:120px;height:36px;border:1px solid #dfe5e2;border-radius:9px;padding:0 10px;font-size:13px;outline:none;font-family:\'JetBrains Mono\',monospace';
const INPUT_ERR = `${INPUT};border-color:${RED}`;
const EARN = 'color:#0d9a68;font-weight:700';
const EARN_ZERO = 'color:#9aa5a1;font-weight:600';

function isZero(v: string): boolean {
  return /^0+(\.0+)?$/.test(v.trim());
}

function productName(lang: Lang, r: { name: string; name_en: string | null }): string {
  return lang === 'en' && r.name_en ? r.name_en : r.name;
}

export function uplineText(t: Dict, v: AgentPricesView | null): string {
  if (!v) return '';
  return v.parent ? interpolate(str(t, 'prUplineAgent'), { name: v.parent.name, biz_no: v.parent.biz_no }) : str(t, 'prUplinePlatform');
}

export interface ReadonlyPriceRow {
  left: string;
  sub: string;
  right: string;
  rightSub: string;
  rightSubStyle: string;
  style: string;
}

// 下级售价只读弹层：开卡各产品 + 末行充值费率；below_parent 行浅红底 + 红字提示
export function readonlyPriceRows(t: Dict, lang: Lang, v: AgentPricesView | null): ReadonlyPriceRow[] {
  if (!v) return [];
  const own = (x: string | null, fmt: (s: string) => string): string => (x === null ? str(t, 'prInherit') : fmt(x));
  const tail = (below: boolean, earnLabel: string, earn: string) => ({
    rightSub: below ? str(t, 'prBelowTip') : `${earnLabel} ${earn}`,
    rightSubStyle: `font-size:11px;margin-top:2px;color:${below ? RED : '#0d9a68'}`,
    style: `display:flex;gap:12px;align-items:center;padding:10px 8px;border-radius:8px;${below ? 'background:#fdf3f3' : ''}`,
  });
  const rows: ReadonlyPriceRow[] = v.open.map((r) => ({
    left: `${productName(lang, r)} · ${r.bin}`,
    sub: `${str(t, 'prColParent')} ${fmtOpenPrice(r.parent_price)} · ${str(t, 'prColMine')} ${own(r.own_price, fmtOpenPrice)}`,
    right: fmtOpenPrice(r.effective_price),
    ...tail(r.below_parent, str(t, 'prColEarn'), fmtOpenPrice(r.earn)),
  }));
  const d = v.deposit;
  rows.push({
    left: str(t, 'prDepositTitle'),
    sub: `${str(t, 'prColParentRate')} ${fmtDepositRate(d.parent_rate)} · ${str(t, 'prColMineRate')} ${own(d.own_rate, fmtDepositRate)}`,
    right: fmtDepositRate(d.effective_rate),
    ...tail(d.below_parent, str(t, 'prColEarnRate'), fmtDepositRate(d.earn)),
  });
  return rows;
}

// ---- 数据 ----

// keepDrafts：1012 / 4220 product_id 刷新时保留用户草稿（只换上级价与生效价）
async function loadPrices(ctx: AgentCtx, opts: { keepDrafts: boolean } = { keepDrafts: false }): Promise<void> {
  const ep = ctx.gp.session.epoch();
  try {
    const view = await ctx.gp.api.call<AgentPricesView>('GET /api/agent/prices');
    if (ctx.gp.session.epoch() !== ep) return;
    const cur = pricesOf(ctx.host.state);
    const fresh = draftsOf(view);
    const drafts = opts.keepDrafts ? { open: { ...fresh.open, ...pickKnown(cur.drafts.open, view) }, deposit: cur.drafts.deposit } : fresh;
    setPr(ctx, { view, drafts, marks: EMPTY_MARKS, ...(opts.keepDrafts ? {} : { error: '' }) });
  } catch (e) {
    if (ctx.gp.session.epoch() === ep) ctx.gp.ui.toast(ctx.gp.ui.err(e));
  }
}

// 只保留仍在上架列表中的产品草稿（已下架产品的草稿丢弃）
function pickKnown(open: Readonly<Record<number, string>>, view: AgentPricesView): Record<number, string> {
  const out: Record<number, string> = {};
  for (const r of view.open) {
    const v = open[r.product_id];
    if (v !== undefined) out[r.product_id] = v;
  }
  return out;
}

async function savePrices(ctx: AgentCtx): Promise<void> {
  const pr = pricesOf(ctx.host.state);
  const t = ctx.t;
  if (!pr.view) return;
  if (!checkDrafts(pr.view, pr.drafts).ok) return setPr(ctx, { error: str(t, 'prFixErrors') });
  const body = pricesPatch(pr.view, pr.drafts);
  if (!body) {
    ctx.gp.ui.toast(str(t, 'prNoChange'));
    return;
  }
  const ep = ctx.gp.session.epoch();
  await ctx.gp.ui.run(async () => {
    try {
      const view = await ctx.gp.api.call<AgentPricesView>('PUT /api/agent/prices', { body });
      if (ctx.gp.session.epoch() !== ep) return;
      setPr(ctx, { view, drafts: draftsOf(view), marks: EMPTY_MARKS, error: '' });
      ctx.gp.ui.toast(str(t, 'toastPricesSaved'));
    } catch (e) {
      if (ctx.gp.session.epoch() !== ep) return;
      const items = priceBelowItems(e);
      if (items.length > 0) {
        setPr(ctx, { marks: belowMarks(items), error: ctx.gp.ui.err(e) });
        return;
      }
      if (e instanceof ApiError && (e.code === 1012 || (e.code === 4220 && e.data.field === 'product_id'))) {
        console.warn('[gp:agent] 售价保存需刷新', e.code, e.data.field ?? '');
        setPr(ctx, { error: str(t, e.code === 1012 ? 'prConflict' : 'prProductGone') });
        await loadPrices(ctx, { keepDrafts: true });
        return;
      }
      setPr(ctx, { error: ctx.gp.ui.err(e) });
    }
  });
}

function onOpen(ctx: AgentCtx, productId: number) {
  return (e: InputEvent): void => {
    const pr = pricesOf(ctx.host.state);
    const open = { ...pr.marks.open };
    delete open[productId];
    setPr(ctx, { drafts: { ...pr.drafts, open: { ...pr.drafts.open, [productId]: e.target.value.slice(0, DRAFT_MAX) } }, marks: { ...pr.marks, open }, error: '' });
  };
}

function onDeposit(ctx: AgentCtx) {
  return (e: InputEvent): void => {
    const pr = pricesOf(ctx.host.state);
    setPr(ctx, { drafts: { ...pr.drafts, deposit: e.target.value.slice(0, DRAFT_MAX) }, marks: { ...pr.marks, deposit: null }, error: '' });
  };
}

const TEXT_KEYS: ReadonlyArray<readonly [string, string]> = [
  ['openTitle', 'prOpenTitle'], ['depositTitle', 'prDepositTitle'], ['colProduct', 'prColProduct'], ['colParent', 'prColParent'], ['colMine', 'prColMine'],
  ['colEffective', 'prColEffective'], ['colEarn', 'prColEarn'], ['colParentRate', 'prColParentRate'], ['colMineRate', 'prColMineRate'],
  ['colEffectiveRate', 'prColEffectiveRate'], ['colEarnRate', 'prColEarnRate'], ['noProducts', 'prNoProducts'], ['note1', 'prNote1'], ['note2', 'prNote2'],
  ['note3', 'prNote3'], ['save', 'prSave'],
];

function errText(t: Dict, e: PriceDraftError, parent: string, fmtKey: 'prErrFormatOpen' | 'prErrFormatRate'): string {
  if (e === 'format') return str(t, fmtKey);
  if (e === 'below') return interpolate(str(t, 'prErrBelow'), { parent });
  return '';
}

export const prices: AgentDomain = {
  name: 'prices',
  keys: KEYS,
  modalKeys: MODAL_KEYS,
  migrated: true,
  i18n: I18N_PRICES,
  state: () => ({ pr: { ...PR0 } }),
  mount(ctx) {
    // 会话结束即复位：下一账号不得看到上一账号的售价与草稿
    ctx.events.on('session:lost', () => ctx.host.setState({ pr: { ...PR0 } }));
    // 每次进入本页重拉（上级可能已调价）；未保存草稿随之丢弃
    ctx.events.on('screen', ({ to }) => {
      if (to === 'prices') void loadPrices(ctx);
    });
  },
  vals(ctx) {
    const { t, s, lang } = ctx;
    const pr = pricesOf(s);
    const v = pr.view;
    const check = v ? checkDrafts(v, pr.drafts) : null;
    const rows = (v?.open ?? []).map((r) => {
      const local = check?.open[r.product_id] ?? '';
      const server = pr.marks.open[r.product_id];
      const err = local ? errText(t, local, fmtOpenPrice(r.parent_price), 'prErrFormatOpen') : server !== undefined ? interpolate(str(t, 'prErrBelow'), { parent: fmtOpenPrice(server) }) : '';
      return {
        name: productName(lang, r),
        bin: r.bin,
        parent: fmtOpenPrice(r.parent_price),
        ph: trimDecimal(r.parent_price, 2),
        draft: pr.drafts.open[r.product_id] ?? '',
        onInput: onOpen(ctx, r.product_id),
        inputStyle: err ? INPUT_ERR : INPUT,
        effective: fmtOpenPrice(r.effective_price),
        earn: fmtOpenPrice(r.earn),
        earnStyle: isZero(r.earn) ? EARN_ZERO : EARN,
        tip: r.below_parent ? str(t, 'prBelowTip') : '',
        err,
        rowStyle: r.below_parent ? ROW_BELOW : ROW,
      };
    });
    const d = v?.deposit ?? null;
    const dLocal = check?.deposit ?? '';
    const dErr = d ? (dLocal ? errText(t, dLocal, fmtDepositRate(d.parent_rate), 'prErrFormatRate') : pr.marks.deposit !== null ? interpolate(str(t, 'prErrBelow'), { parent: fmtDepositRate(pr.marks.deposit) }) : '') : '';
    // 新增静态文案经 vals 下发（build split 双语守卫：模板 t.* 只直引原型 / 已登记扩展字典键）
    const prText = Object.fromEntries(TEXT_KEYS.map(([k, key]) => [k, str(t, key)]));
    return {
      prText,
      prUpline: uplineText(t, v),
      prRows: rows,
      prNoRows: !!v && v.open.length === 0,
      prDeposit: {
        show: !!d,
        parent: d ? fmtDepositRate(d.parent_rate) : '—',
        ph: d ? trimDecimal(d.parent_rate, 4) : '',
        draft: pr.drafts.deposit,
        onInput: onDeposit(ctx),
        inputStyle: dErr ? INPUT_ERR : INPUT,
        effective: d ? fmtDepositRate(d.effective_rate) : '—',
        earn: d ? fmtDepositRate(d.earn) : '—',
        earnStyle: d && !isZero(d.earn) ? EARN : EARN_ZERO,
        tip: d?.below_parent ? str(t, 'prBelowTip') : '',
        err: dErr,
        rowStyle: d?.below_parent ? ROW_BELOW : ROW,
      },
      prError: pr.error,
      prSave: () => void savePrices(ctx),
    };
  },
};
